import { useMediaQuery } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StockCountItem, StockStatusType } from '../../../hooks';
import { Typography } from '../../../stories/atoms';
import { RowFlexContainer } from '../../../stories/atoms/containerV2/ContainerV2';
import { DropdownMenu, FilterDrawer, FilterType } from '../../../stories/molecules';
import { muiTheme } from '../../../theme';
import { yearlyStockTakeTranslations } from '../../../translations';

export enum StockDropdownType {
  DEPARTMENT = 'department',
  INDEX = 'index',
  SUBINDEX = 'subindex',
  STATUS = 'status',
}
type DropdownProps = {
  type: StockDropdownType;
  label: string;
  items: string[] | StockStatusType[];
  selectedItems: string[] | StockStatusType[];
  handleDropdown: (type: StockDropdownType, selectedItems: string[] | StockStatusType[]) => void;
  testId: string;
};

export type StockDropdownProps = {
  stockCount: StockCountItem[];
  disabled: boolean;
  selectedDepartment: string[];
  selectedIndex: string[];
  selectedSubindex: string[];
  isFilterDrawerOpen: boolean;
  clearAllFilters: () => void;
  onClose: () => void;
  handleDropdown: (type: StockDropdownType, selectedItems: string[] | StockStatusType[]) => void;
};
export const StockDropdown: FC<StockDropdownProps> = ({
  stockCount,
  disabled,
  selectedDepartment,
  selectedIndex,
  selectedSubindex,
  isFilterDrawerOpen,
  clearAllFilters,
  onClose,
  handleDropdown,
}) => {
  const { t } = useTranslation();
  const { status: statusTranslations, stockTable: stockTableTranslations } = yearlyStockTakeTranslations;
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

  const departments = [...new Set(stockCount.map((item) => item.departmentName))];
  const indexes = [...new Set(stockCount.map((item) => item.index))];
  const subIndexes = [...new Set(stockCount.map((item) => item.subIndex))];
  const dropdownConfigs: DropdownProps[] = [
    {
      type: StockDropdownType.DEPARTMENT,
      label: t(stockTableTranslations.department.key, stockTableTranslations.department.defaultValue),
      items: departments,
      selectedItems: selectedDepartment,
      testId: 'select-department-dropdown',
      handleDropdown,
    },
    {
      type: StockDropdownType.INDEX,
      label: t(stockTableTranslations.index.key, stockTableTranslations.index.defaultValue),
      items: indexes,
      selectedItems: selectedIndex,
      testId: 'select-index-dropdown',
      handleDropdown,
    },
    {
      type: StockDropdownType.SUBINDEX,
      label: t(stockTableTranslations.subindex.key, stockTableTranslations.subindex.defaultValue),
      items: subIndexes,
      selectedItems: selectedSubindex,
      testId: 'select-subindex-dropdown',
      handleDropdown,
    },
  ];

  const statusTranslationMapping: Record<StockStatusType, string> = {
    Pick: t(statusTranslations.pickStatus.key, statusTranslations.pickStatus.defaultValue),
    Picked: t(statusTranslations.pickedStatus.key, statusTranslations.pickedStatus.defaultValue),
    NotFound: t(statusTranslations.notFoundStatus.key, statusTranslations.notFoundStatus.defaultValue),
  };

  // check if item is a StockStatusType
  const isStockStatusType = (item: string | StockStatusType): item is StockStatusType => {
    return ['Pick', 'Picked', 'NotFound'].includes(item as StockStatusType);
  };

  return (
    <RowFlexContainer data-testid="stock-dropdown-wrapper" gap={[7]}>
      {!isMobile &&
        dropdownConfigs.map((config) => (
          <DropdownMenu
            key={config.label}
            data-testid={config.testId}
            dropdownLabel={config.label}
            menuItems={config.items.map((item: string | StockStatusType) => {
              return {
                key: item,
                item: (
                  <Typography margin={[0, 4]}>
                    {isStockStatusType(item) ? statusTranslationMapping[item as StockStatusType] : item}
                  </Typography>
                ),
              };
            })}
            minWidth={'0px'}
            multiSelect
            checkbox
            isCompact
            maxHeight={'50vh'}
            initialSelected={config.selectedItems}
            onSelect={(selectedItems) => {
              handleDropdown(config.type, selectedItems);
            }}
            buttonContentProps={{
              isDisabled: disabled,
            }}
            isOnlyIcon
            allowTooltip
          />
        ))}
      {isMobile && (
        <FilterDrawer
          isOpen={isFilterDrawerOpen}
          onDrawerClose={onClose}
          onClearAllFilters={clearAllFilters}
          filtersSpecs={dropdownConfigs.map((config) => {
            return {
              key: config.testId,
              type: FilterType.MultiSelectList,
              title: config.label,
              options: config.items.map((item: string | StockStatusType) => ({
                key: item,
                title: isStockStatusType(item) ? statusTranslationMapping[item] : item,
              })),
              selectedOptionsKeys: config.selectedItems,
              onSelect: (selectedItemKeys) => handleDropdown(config.type, selectedItemKeys),
            };
          })}
        />
      )}
    </RowFlexContainer>
  );
};
